import { round2 } from "@medicorepay/shared";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showToast } from "../../lib/toast";
import {
  GoodsReceipt,
  goodsReceiptService,
} from "../../services/goods-receipt.service";
import {
  purchaseReturnService,
  ReturnReason,
} from "../../services/purchase-return.service";

const RETURN_REASON_OPTIONS: Array<{ value: ReturnReason; label: string }> = [
  { value: "DAMAGED", label: "Damaged" },
  { value: "DEFECTIVE", label: "Defective" },
  { value: "EXPIRED", label: "Expired" },
  { value: "WRONG_ITEM", label: "Wrong Item" },
  { value: "QUALITY_ISSUE", label: "Quality Issue" },
  { value: "OVERSTOCK", label: "Overstock" },
  { value: "RECALL", label: "Recall" },
  { value: "OTHER", label: "Other" },
];

interface ReturnLine {
  goodsReceiptItemId: string;
  productId: string;
  batchId: string;
  productName: string;
  batchNumber: string;
  availableQty: number;
  returnableBuckets: {
    available: number;
    quarantine: number;
    damaged: number;
  };
  originalBucket: "available" | "quarantine" | "damaged";
  qtyReturned: number;
  unitPrice: number; // Per base-unit price
  discountPercent: number;
  schemeDiscountPercent: number;
  taxPercent: number;
  reason?: string;
}

export function CreatePurchaseReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const grnId = searchParams.get("grnId");
  const [receipt, setReceipt] = useState<GoodsReceipt | null>(null);
  const [loading, setLoading] = useState(false);
  const [returnReason, setReturnReason] = useState<ReturnReason>("DAMAGED");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<ReturnLine[]>([]);

  const getAvailableBaseQty = (item: any) => {
    if (item.returnableQuantity !== undefined) {
      return Number(item.returnableQuantity);
    }
    const unitsPerPack = Number(item.unitsPerPack || 1);
    const storedBaseQuantity = Number(item.baseQuantityReceived || 0);

    if (storedBaseQuantity > 0) {
      return storedBaseQuantity;
    }

    const fallbackReceived = Number(item.receivedQty ?? item.quantity ?? 0);
    return fallbackReceived * (unitsPerPack > 0 ? unitsPerPack : 1);
  };

  useEffect(() => {
    if (!grnId) return;
    const loadReceipt = async () => {
      try {
        setLoading(true);
        const data = await goodsReceiptService.getGoodsReceipt(grnId);
        setReceipt(data);
        const mappedLines = data.items
          .filter((item) => item.batchId)
          .map((item) => {
            const unitsPerPack = Number(item.unitsPerPack || 1);
            const packPrice = Number(item.unitPrice);
            // Convert per-pack price to per-base-unit price
            const perUnitPrice =
              unitsPerPack > 1
                ? Math.round((packPrice / unitsPerPack) * 100) / 100
                : packPrice;
            const returnableBuckets = item.returnableBuckets ?? {
              available: getAvailableBaseQty(item),
              quarantine: 0,
              damaged: 0,
            };
            const originalBucket = (
              returnableBuckets.available > 0
                ? "available"
                : returnableBuckets.quarantine > 0
                  ? "quarantine"
                  : "damaged"
            ) as "available" | "quarantine" | "damaged";
            return {
              goodsReceiptItemId: item.id,
              productId: item.productId,
              batchId: item.batchId as string,
              productName: item.product.name,
              batchNumber: item.batchNumber,
              availableQty: Number(returnableBuckets[originalBucket] || 0),
              returnableBuckets,
              originalBucket,
              qtyReturned: 0,
              unitPrice: perUnitPrice,
              discountPercent: Number(item.discountPercent || 0),
              schemeDiscountPercent: Number(item.schemeDiscountPercent || 0),
              taxPercent: Number(item.taxPercent),
            };
          })
          .filter((line) => line.availableQty > 0);

        if (mappedLines.length === 0) {
          showToast.error("No eligible batches found for return");
        }

        setLines(mappedLines);
      } catch (err: any) {
        showToast.error(
          err.response?.data?.message || "Failed to load goods receipt",
        );
        navigate("/goods-receipts");
      } finally {
        setLoading(false);
      }
    };

    loadReceipt();
  }, [grnId, navigate]);

  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        // Calculate with both discounts to match backend logic (use round2 for consistency)
        const lineSubtotal = round2(line.qtyReturned * line.unitPrice);
        const lineDiscount = round2(
          (lineSubtotal * line.discountPercent) / 100,
        );
        const afterTradeDiscount = round2(lineSubtotal - lineDiscount);
        const schemeDiscount = round2(
          (afterTradeDiscount * line.schemeDiscountPercent) / 100,
        );
        const taxableAmount = round2(afterTradeDiscount - schemeDiscount);
        const taxAmount = round2((taxableAmount * line.taxPercent) / 100);
        acc.subtotal += lineSubtotal;
        acc.discount += lineDiscount + schemeDiscount;
        acc.tax += taxAmount;
        acc.total += taxableAmount + taxAmount;
        return acc;
      },
      { subtotal: 0, discount: 0, tax: 0, total: 0 },
    );
  }, [lines]);

  const handleQtyChange = (index: number, value: number) => {
    setLines((prev) => {
      const updated = [...prev];
      const safeValue = Math.max(
        0,
        Math.min(value, updated[index].availableQty),
      );
      updated[index] = { ...updated[index], qtyReturned: safeValue };
      return updated;
    });
  };

  const handleReasonChange = (index: number, value: string) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], reason: value };
      return updated;
    });
  };

  const handleBucketChange = (
    index: number,
    value: "available" | "quarantine" | "damaged",
  ) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        originalBucket: value,
        availableQty: updated[index].returnableBuckets[value],
        qtyReturned: 0,
      };
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!grnId) {
      showToast.error("Please select a GRN to create a return");
      return;
    }

    const returnItems = lines
      .filter((line) => line.qtyReturned > 0)
      .map((line) => ({
        productId: line.productId,
        batchId: line.batchId,
        goodsReceiptItemId: line.goodsReceiptItemId,
        lotId: line.batchId,
        quantity: String(line.qtyReturned),
        originalBucket: line.originalBucket,
        reason: line.reason,
      }));

    if (returnItems.length === 0) {
      showToast.error("Please enter at least one return quantity");
      return;
    }

    try {
      setLoading(true);
      const created = await purchaseReturnService.createPurchaseReturn(grnId, {
        returnReason,
        notes: notes || undefined,
        items: returnItems,
      });
      showToast.success("Purchase return created successfully");
      navigate(`/purchase-returns/${created.id}`);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to create return");
    } finally {
      setLoading(false);
    }
  };

  if (!grnId) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Goods Receipt
          </h1>
          <p className="text-gray-500 mb-4">
            Purchase returns are created from a completed GRN.
          </p>
          <button
            onClick={() => navigate("/goods-receipts")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Goods Receipts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/goods-receipts/${grnId}`)}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to GRN
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Create Purchase Return
        </h1>
        {receipt && (
          <p className="text-sm text-gray-500 mt-1">
            GRN #{receipt.grnNumber} · {receipt.supplier.name}
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Reason
            </label>
            <select
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value as ReturnReason)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {RETURN_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Return Items
        </h2>
        {loading && (
          <div className="text-center py-6 text-gray-500">Loading...</div>
        )}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Product
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Batch
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Available
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Stock Bucket
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Return Qty
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Unit Price
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Tax %
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((line, index) => (
                  <tr key={`${line.productId}-${line.batchId}`}>
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {line.productName}
                        </p>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-mono">
                        {line.batchNumber}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {line.availableQty}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <select
                        value={line.originalBucket}
                        onChange={(e) =>
                          handleBucketChange(
                            index,
                            e.target.value as
                              | "available"
                              | "quarantine"
                              | "damaged",
                          )
                        }
                        className="px-2 py-1 border rounded"
                      >
                        {(["available", "quarantine", "damaged"] as const).map(
                          (bucket) => (
                            <option
                              key={bucket}
                              value={bucket}
                              disabled={line.returnableBuckets[bucket] <= 0}
                            >
                              {bucket} ({line.returnableBuckets[bucket]})
                            </option>
                          ),
                        )}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <input
                        type="number"
                        min={0}
                        max={line.availableQty}
                        value={line.qtyReturned}
                        onChange={(e) =>
                          handleQtyChange(index, Number(e.target.value))
                        }
                        className="w-20 px-2 py-1 border rounded text-center"
                      />
                    </td>
                    <td className="px-3 py-3 text-right">
                      ₹{line.unitPrice.toFixed(2)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {line.taxPercent.toFixed(2)}%
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={line.reason || ""}
                        onChange={(e) =>
                          handleReasonChange(index, e.target.value)
                        }
                        placeholder="Optional"
                        className="w-full px-2 py-1 border rounded"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">Subtotal</p>
          <p className="text-lg font-semibold">₹{totals.subtotal.toFixed(2)}</p>
          {totals.discount > 0 && (
            <>
              <p className="text-sm text-gray-500 mt-2">Discount</p>
              <p className="text-lg font-semibold text-green-600">
                -₹{totals.discount.toFixed(2)}
              </p>
            </>
          )}
          <p className="text-sm text-gray-500 mt-2">Tax</p>
          <p className="text-lg font-semibold">₹{totals.tax.toFixed(2)}</p>
          <p className="text-sm text-gray-500 mt-2">Total</p>
          <p className="text-xl font-bold text-gray-900">
            ₹{totals.total.toFixed(2)}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/goods-receipts/${grnId}`)}
            className="px-4 py-2 border rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Create Return"}
          </button>
        </div>
      </div>
    </div>
  );
}
